import { useEffect, useState } from "react";
import { Mail, Save, Send, MailCheck, MailWarning } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Field = ({ label, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;

export default function EmailSettings() {
  const { user } = useAuth();
  const [form, setForm] = useState({ host: "", port: "465", user: "", password: "", sender: "", backup_alert_to: "" });
  const [info, setInfo] = useState({ has_password: false, configured: false });
  const [testTo, setTestTo] = useState(user?.email || "");
  const [busy, setBusy] = useState("");

  const apply = (d) => { setForm({ host: d.host, port: d.port || "465", user: d.user, password: "", sender: d.sender, backup_alert_to: d.backup_alert_to || "" }); setInfo({ has_password: d.has_password, configured: d.configured }); };
  useEffect(() => { api.get("/settings/smtp").then((r) => apply(r.data)).catch(() => {}); }, []);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const save = async () => {
    setBusy("save");
    try { const { data } = await api.put("/settings/smtp", form); apply(data); toast.success("Parametri email salvati"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try { await api.post("/settings/smtp/test", { to: testTo }); toast.success(`Mail di prova inviata a ${testTo}`); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="email-settings">
      <div className="mb-1 flex items-center gap-2"><Mail size={18} className="text-primary" /><h3 className="font-bold text-navy">Posta in uscita (SMTP)</h3></div>
      <p className="mb-4 text-sm text-muted-foreground">Casella aziendale usata per le mail ai responsabili. Lascia vuota la password per mantenere quella salvata.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Server SMTP"><Input value={form.host} onChange={set("host")} placeholder="smtp.dominio.it" data-testid="smtp-host" /></Field>
        <Field label="Porta">
          <Select value={form.port} onValueChange={set("port")}>
            <SelectTrigger data-testid="smtp-port"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="465">465 · SSL</SelectItem><SelectItem value="587">587 · STARTTLS</SelectItem></SelectContent>
          </Select>
        </Field>
        <Field label="Utente (indirizzo casella)"><Input value={form.user} onChange={set("user")} data-testid="smtp-user" /></Field>
        <Field label="Password"><Input type="password" value={form.password} onChange={set("password")} placeholder={info.has_password ? "•••••••• (salvata)" : ""} autoComplete="new-password" data-testid="smtp-password" /></Field>
        <Field label="Mittente (nome o indirizzo)"><Input value={form.sender} onChange={set("sender")} placeholder="FLUX - POWERCOM" data-testid="smtp-sender" /></Field>
        <Field label="Destinatario allerte backup fallito"><Input value={form.backup_alert_to} onChange={set("backup_alert_to")} placeholder="nome@dominio.it" data-testid="smtp-backup-alert-to" /></Field>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button onClick={save} disabled={!!busy} data-testid="smtp-save"><Save size={16} />Salva</Button>
        {info.configured
          ? <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><MailCheck size={14} />Configurata</span>
          : <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-700"><MailWarning size={14} />Non configurata</span>}
      </div>
      <div className="mt-5 flex flex-wrap items-end gap-3 border-t border-border pt-4">
        <Field label="Invia mail di prova a"><Input value={testTo} onChange={(e) => setTestTo(e.target.value)} className="w-72" data-testid="smtp-test-to" /></Field>
        <Button variant="outline" onClick={test} disabled={!!busy || !testTo} data-testid="smtp-test-btn"><Send size={16} />{busy === "test" ? "Invio…" : "Invia prova"}</Button>
      </div>
    </div>
  );
}
